// vendor
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { showErrorNotificationDialogAction } from 'app/store/global/dialog/dialog.action';
import { clockOut } from 'app/store/timesheet/timesheet.action';
import { tap } from 'rxjs';
import moment from 'moment';
import { TimesheetTypes } from 'app/store/timesheet/timesheet.type';
import { Actions, ofType } from '@ngrx/effects';
import { Router, ActivatedRoute } from '@angular/router';
import { ROUTES } from 'app/utils/constants/routes.constants';
import { hashConfig } from 'app/utils/config/hash.config';
import { getAllRouteParams } from 'app/utils/helpers/helper-functions';

@Component({
  selector: 'app-add-break',
  templateUrl: './add-break.component.html',
  styleUrls: ['./add-break.component.scss'],
  standalone: false,
})
export class AddBreakComponent implements OnInit {
  companyID: any;
  currentFieldName = 'break_start';
  input: string = '';
  time: string = '00:00';
  meridian: 'AM' | 'PM' = 'AM';
  activeField: 'break_start' | 'break_end' = 'break_start';
  errorMessage = null;
  break_start: any = {
    time: '00:00',
    meridian: 'AM',
    isValid: true,
  };
  break_end = {
    time: '00:00',
    meridian: 'AM',
    isValid: true,
  };
  timesheetID!: number;
  isLoading = false;

  constructor(
    private store: Store,
    private router: Router,
    private actions: Actions,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const routeParams = getAllRouteParams(this.route);
    this.companyID = routeParams['companyID'];
    const paramTimesheetID: any = this.route.snapshot.paramMap.get('id');
    this.timesheetID = Number(hashConfig.decrypt(paramTimesheetID)[0]);
  }

  onHandleValidateBreakTime($event: any): void {
    this.break_start = $event.target.value;
  }

  onHandleSelectInput(name: any) {
    this.currentFieldName = name;
  }

  onHandleKeyPress(key: string) {
    if (this.input.length < 4) {
      this.input += key;
      this.formatTime();
    }
  }

  onHandleOk() {
    if (
      this.break_start.isValid &&
      this.break_start.time != '00:00' &&
      this.break_end.isValid &&
      this.break_end.time != '00:00'
    ) {
      const error = this.validateBreakTimes();
      if (error) {
        this.store.dispatch(
          showErrorNotificationDialogAction({
            message: error,
          }),
        );
        return;
      }

      const break_time = {
        break_start: moment(
          `${this.break_start.time} ${this.break_start.meridian}`,
          'h:mm A',
        ).format('HH:mm'),
        break_end: moment(
          `${this.break_end.time} ${this.break_end.meridian}`,
          'h:mm A',
        ).format('HH:mm'),
      };
      this.isLoading = true;
      this.store.dispatch(
        clockOut({
          mood: this.route.snapshot.queryParamMap.get('mood'),
          timesheetID: this.timesheetID,
          ...break_time,
          ...(this.route.snapshot.queryParamMap.get('note')?.trim() && {
            note: this.route.snapshot.queryParamMap.get('note'),
            ...(this.route.snapshot.queryParamMap.get('fileName') && {
              clockOutSelfie: this.route.snapshot.queryParamMap.get('fileName'),
            }),
          }),
        }),
      );

      this.actions
        .pipe(
          ofType(TimesheetTypes.POST_CLOCK_OUT_CANCEL),
          tap(() => {
            this.isLoading = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe();

      this.actions
        .pipe(
          ofType(TimesheetTypes.POST_CLOCK_OUT_SUCCESS),
          tap(() => {
            this.isLoading = false;
            this.cdr.markForCheck();
            this.router.navigate([`${this.companyID}/dashboard`]);
          }),
        )
        .subscribe();
    } else if (
      this[this.activeField].isValid &&
      this[this.activeField].time != '00:00'
    ) {
      this.activeField = 'break_end';
      this.input = '';
    }
  }

  onHandleClear() {
    this.input = '';
    this[this.activeField].time = '00:00';
    this[this.activeField].isValid = true;
  }

  formatTime() {
    const padded = this.input.padStart(4, '0');
    let hours = parseInt(padded.slice(0, 2), 10);
    const minutes = parseInt(padded.slice(2, 4), 10);

    // Validate ranges for 12-hour format
    if (hours < 1 || hours > 12 || minutes > 59) {
      this[this.activeField].isValid = false;
    } else {
      this[this.activeField].isValid = true;
    }

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    this[this.activeField].time = `${hh}:${mm}`;
  }

  onHandleChangeMeridian(key: 'break_start' | 'break_end') {
    this[key].meridian = this[key].meridian === 'AM' ? 'PM' : 'AM';
  }

  onHandleSelectActiveField(key: 'break_start' | 'break_end') {
    this.activeField = key;
    this.input = '';
    this[this.activeField].time = '00:00';
  }

  validateBreakTimes(): string | null {
    const start = this.convertTo24Hour(this.break_start);
    const end = this.convertTo24Hour(this.break_end);

    if (!start || !end) {
      return 'One or both break times are invalid.';
    }

    const startMinutes = this.timeToMinutes(start);
    const endMinutes = this.timeToMinutes(end);

    if (startMinutes >= endMinutes) {
      return 'Break end must be after break start.';
    }

    return null; // ✅ No error
  }

  convertTo24Hour(timeField: any): string | null {
    const timeParts = timeField.time.split(':');
    if (timeParts.length !== 2) return null;

    let hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;

    const meridian = timeField.meridian;

    if (meridian === 'PM' && hours < 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  }

  timeToMinutes(time: string): number {
    const [hh, mm] = time.split(':').map((part) => parseInt(part, 10));
    return hh * 60 + mm;
  }

  onHandleBack() {
    this.router.navigate([`${this.companyID}/${ROUTES.DASHBOARD}`]);
  }
}
